/**
=========================================================
* Otis Admin PRO - v2.0.1
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
// @mui material components
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import { useMediaQuery } from "@mui/material";

// Otis Admin PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import { useTranslation } from "react-i18next";
import palette from "assets/theme/base/palette";

function DefaultProjectCard({
  item,
  image,
  liked,
  disliked,
  saved,
  viewed,
  getContentLinkHandler,
  likeHandler,
  dislikeHandler,
  removeLikeHandler,
  removeDislikeHandler,
  saveHandler,
  shareHandler,
  editable,
  editContentHandler,
  type,
  label,
  title,
  shareable,
  description,
  content_url,
  thumbnail_url,
  action,
  authors,
  icon,
  gamePlanType,
  weekIndex,
  showTypeLabel,
  viewedChip,
}) {
  // const [like,  setLike] = useState(liked)
  // const [dislike, setDislike] = useState(disliked)
  const { t } = useTranslation();
  const [save, setSave] = useState(saved);
  const matches = useMediaQuery("(max-width:920px)");
  const renderAuthors = authors.map(({ image: media, name }) => (
    <Tooltip key={name} title={name} placement="bottom">
      <MDAvatar
        src={media}
        alt={name}
        size="xs"
        sx={({ borders: { borderWidth }, palette: { white } }) => ({
          border: `${borderWidth[2]} solid ${white.main}`,
          cursor: "pointer",
          position: "relative",
          ml: -1.25,

          "&:hover, &:focus": {
            zIndex: "10",
          },
        })}
      />
    </Tooltip>
  ));

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        border: "1.5px solid rgba(0,0,0,0.10)",
        borderRadius: "20px",
        overflow: "hidden",
        height: "100%",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
          borderColor: "rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── Thumbnail with overlays ── */}
      <MDBox position="relative" sx={{ flexShrink: 0 }}>
        <MDBox
          sx={{
            width: "100%",
            height: 200,
            bgcolor: "#eef4f8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <CardMedia
            src={image}
            component="img"
            title={title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
              p: 1,
            }}
          />
        </MDBox>

        {/* Top-left: white pill chip with icon + type label */}
        {icon && type && (
          <MDBox
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              bgcolor: "#fff",
              borderRadius: "20px",
              px: 1.2,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
            }}
          >
            <Icon sx={{ color: palette.brand[500], fontSize: "16px !important" }}>{icon}</Icon>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{ color: "text.primary", textTransform: "uppercase", fontSize: "0.68rem", letterSpacing: "0.04em" }}
            >
              {type}
            </MDTypography>
          </MDBox>
        )}

        {/* Top-right: viewed + bookmark + share as white circles */}
        <MDBox
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {viewed && (
            <Tooltip title={t("contentCard.viewed")}>
              <MDBox
                sx={{
                  bgcolor: "#fff",
                  border: "1.5px solid rgba(0,0,0,0.12)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                }}
              >
                <Icon sx={{ color: palette.brand[500], fontSize: "18px !important" }}>visibility</Icon>
              </MDBox>
            </Tooltip>
          )}

          <Tooltip title={saved ? t("contentCard.removeFromWatchlist") : t("contentCard.saveToWatchlist")}>
            <MDBox
              onClick={() => saveHandler(title)}
              sx={{
                bgcolor: "#fff",
                border: "1.5px solid rgba(0,0,0,0.12)",
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                transition: "all 180ms ease",
                "&:hover": { borderColor: palette.orange[400], bgcolor: palette.orange[50] },
              }}
            >
              <Icon sx={{ color: saved ? palette.orange[500] : "rgba(0,0,0,0.55)", fontSize: "18px !important" }}>
                {saved ? "bookmark" : "bookmark_border"}
              </Icon>
            </MDBox>
          </Tooltip>

          {shareable && (
            <Tooltip title={t("contentCard.recommend")}>
              <MDBox
                onClick={() => shareHandler(title)}
                sx={{
                  bgcolor: "#fff",
                  border: "1.5px solid rgba(0,0,0,0.12)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                  transition: "all 180ms ease",
                  "&:hover": { borderColor: palette.brand[400], bgcolor: palette.brand[50] },
                }}
              >
                <Icon sx={{ color: "rgba(0,0,0,0.55)", fontSize: "16px !important" }}>send</Icon>
              </MDBox>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>

      {/* ── Body ── */}
      <MDBox
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          px: 2,
          pt: 2,
          pb: 2,
          gap: 1,
        }}
      >
        {/* Title */}
        <MDTypography
          variant="h6"
          fontWeight="bold"
          sx={{
            color: "text.primary",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {title}
        </MDTypography>

        {/* Viewed chip */}
        {viewedChip && (
          <MDBox mt={0.5}>
            <MDBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: palette.orange[50],
                border: `1px solid ${palette.orange[200]}`,
                borderRadius: "20px",
                px: 1.2,
                py: 0.3,
              }}
            >
              <Icon sx={{ color: palette.orange[500], fontSize: "14px !important" }}>visibility</Icon>
              <MDTypography variant="caption" fontWeight="bold" sx={{ color: palette.orange[500], fontSize: "0.68rem" }}>
                {t("contentCard.viewed", { defaultValue: "Viewed" })}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        {/* Content type row */}
        {showTypeLabel && icon && type && (
          <MDBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <Icon sx={{ color: "text.secondary", fontSize: "16px !important" }}>{icon}</Icon>
            <MDTypography variant="caption" sx={{ color: "text.secondary" }}>{type}</MDTypography>
          </MDBox>
        )}

        {/* Divider */}
        <MDBox sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", mt: 0.5, mb: 0.5 }} />

        {/* Action row */}
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          {/* View button */}
          {action.type === "internal" ? (
            <MDButton
              component={Link}
              to={action.route}
              variant="outlined"
              size="small"
              color="primary"
              sx={{ borderRadius: "10px", px: 2.5, fontSize: "0.8rem", fontWeight: 600 }}
              startIcon={<Icon sx={{ fontSize: "18px !important" }}>visibility</Icon>}
            >
              {action.label}
            </MDButton>
          ) : (
            <MDButton
              component="button"
              onClick={() => getContentLinkHandler(item, gamePlanType, weekIndex)}
              variant="outlined"
              size="small"
              color="primary"
              sx={{ borderRadius: "10px", px: 2.5, fontSize: "0.8rem", fontWeight: 600 }}
              startIcon={<Icon sx={{ fontSize: "18px !important" }}>visibility</Icon>}
            >
              {action.label}
            </MDButton>
          )}

          {/* Like / Dislike icon buttons */}
          <MDBox display="flex" gap={0.75}>
            <Tooltip title={liked ? t("contentCard.removeLike") : t("contentCard.like")}>
              <MDBox
                onClick={() => (liked ? removeLikeHandler(title) : likeHandler(title))}
                sx={({ palette }) => ({
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  border: `1.5px solid ${liked ? palette.primary.main : palette.grey[300]}`,
                  bgcolor: liked ? `${palette.primary.main}15` : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 180ms ease",
                  "&:hover": { borderColor: palette.primary.main, bgcolor: `${palette.primary.main}15` },
                })}
              >
                {liked ? (
                  <ThumbUpIcon sx={({ palette }) => ({ fontSize: "18px !important", color: palette.primary.main })} />
                ) : (
                  <ThumbUpOutlinedIcon sx={{ fontSize: "18px !important", color: "text.secondary" }} />
                )}
              </MDBox>
            </Tooltip>

            <Tooltip title={disliked ? t("contentCard.removeDislike") : t("contentCard.dislike")}>
              <MDBox
                onClick={() => (disliked ? removeDislikeHandler(title) : dislikeHandler(title))}
                sx={({ palette }) => ({
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  border: `1.5px solid ${disliked ? palette.error.main : palette.grey[300]}`,
                  bgcolor: disliked ? `${palette.error.main}15` : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 180ms ease",
                  "&:hover": { borderColor: palette.error.main, bgcolor: `${palette.error.main}15` },
                })}
              >
                {disliked ? (
                  <ThumbDownIcon sx={({ palette }) => ({ fontSize: "18px !important", color: palette.error.main })} />
                ) : (
                  <ThumbDownOutlinedIcon sx={{ fontSize: "18px !important", color: "text.secondary" }} />
                )}
              </MDBox>
            </Tooltip>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of DefaultProjectCard
DefaultProjectCard.defaultProps = {
  item: {},
  editable: false,
  shareable: true,
  liked: false,
  disliked: false,
  saved: false,
  viewed: false,
  showTypeLabel: true,
  viewedChip: false,
  likeHandler: () => {},
  dislikeHandler: () => {},
  removeLikeHandler: () => {},
  removeDislikeHandler: () => {},
  saveHandler: () => {},
  shareHandler: () => {},
  authors: [],
  getContentLinkHandler: () => {},
  editContentHandler: () => {},
  gamePlanType: "myGamePlan",
  weekIndex: 0,
};

// Typechecking props for the DefaultProjectCard
DefaultProjectCard.propTypes = {
  item: PropTypes.object,
  icon: PropTypes.string,
  image: PropTypes.string,
  liked: PropTypes.bool,
  disliked: PropTypes.bool,
  saved: PropTypes.bool,
  viewed: PropTypes.bool,
  likeHandler: PropTypes.func,
  dislikeHandler: PropTypes.func,
  removeLikeHandler: PropTypes.func,
  removeDislikeHandler: PropTypes.func,
  saveHandler: PropTypes.func,
  shareHandler: PropTypes.func,
  label: PropTypes.string,
  title: PropTypes.string.isRequired,
  shareable: PropTypes.bool,
  description: PropTypes.string,
  getContentLinkHandler: PropTypes.func,
  editContentHandler: PropTypes.func,
  editable: PropTypes.bool,
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]),
    route: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
      "white",
    ]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  authors: PropTypes.instanceOf(Array),
  gamePlanType: PropTypes.string,
  weekIndex: PropTypes.number,
};

export default DefaultProjectCard;
